import React from 'react';
import { Alert, Space, Typography } from 'antd';
import { calculateSMSSegments, getSegmentColor } from '../utils/smsSegmentCalculator';

const { Text } = Typography;

/**
 * SMS Segment Indicator Component
 * Displays segment count, character usage, encoding type, and cost estimation
 */
const SMSSegmentIndicator = ({ message, recipientCount = 1, showCost = true }) => {
  const segmentInfo = calculateSMSSegments(message || '');
  const { 
    segments, 
    charactersUsed, 
    charactersRemaining, 
    encoding, 
    isMultiSegment, 
    estimatedCost,
    warnings 
  } = segmentInfo;

  if (!message || message.length === 0) {
    return (
      <div style={{ 
        padding: '8px 12px', 
        backgroundColor: '#f5f5f5', 
        borderRadius: '4px',
        fontSize: '12px',
        color: '#666'
      }}>
        Enter a message to see segment information
      </div>
    );
  }

  const totalCost = estimatedCost * recipientCount;
  const segmentColor = getSegmentColor(segments);

  return (
    <div style={{ marginTop: '8px' }}>
      {/* Main segment info */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '8px 12px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: `1px solid ${segmentColor}`,
        fontSize: '12px'
      }}>
        <Space>
          <Text style={{ color: segmentColor, fontWeight: 'bold' }}>
            {segments} segment{segments > 1 ? 's' : ''}
          </Text>
          <Text type="secondary">
            {charactersUsed} chars ({encoding})
          </Text>
          {charactersRemaining < 20 && (
            <Text style={{ color: '#fa8c16' }}>
              {charactersRemaining} remaining
            </Text>
          )}
        </Space>
        
        {showCost && recipientCount > 0 && (
          <Text style={{ fontWeight: 'bold' }}>
            ~${totalCost.toFixed(3)} total
            {recipientCount > 1 && (
              <span style={{ fontSize: '11px', color: '#666' }}>
                {' '}(${estimatedCost.toFixed(3)} × {recipientCount})
              </span>
            )}
          </Text>
        )}
      </div>

      {/* Warnings for multi-segment messages */}
      {isMultiSegment && (
        <Alert
          message="Multi-segment SMS"
          description={
            <div>
              <div>This message will be sent as {segments} separate SMS segments.</div>
              <div style={{ marginTop: '4px', fontSize: '11px' }}>
                • Higher delivery cost ({segments}× standard rate)
                • Some carriers may display segments out of order
                • Consider shortening message for better delivery
              </div>
            </div>
          }
          type="warning"
          showIcon
          size="small"
          style={{ marginTop: '8px' }}
        />
      )}

      {/* Character encoding warnings */}
      {encoding === 'Unicode' && (
        <Alert
          message="Unicode characters detected"
          description="Unicode characters reduce the character limit to 70 per segment (vs 160 for GSM-7)"
          type="info"
          showIcon
          size="small"
          style={{ marginTop: '8px' }}
        />
      )}

      {/* Additional warnings */}
      {warnings.filter(w => !w.includes('segments') && !w.includes('Cost')).length > 0 && (
        <div style={{ marginTop: '8px' }}>
          {warnings
            .filter(w => !w.includes('segments') && !w.includes('Cost'))
            .map((warning, index) => (
              <div key={index} style={{ fontSize: '11px', color: '#fa8c16' }}>
                ⚠️ {warning}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default SMSSegmentIndicator; 